import type { ApiResponse, TenantUser } from './types';
import { DEFAULT_STATION_LAYOUT, resolveStationLayoutId } from './presentation';

export type { ApiResponse, TenantUser } from './types';

export const API = '';

/** Turn opaque backend messages (request failed / empty) into a toast-safe Chinese string. */
export function friendlyApiMessage(message?: string, fallback = '操作失败，请稍后重试'): string {
  const m = String(message || '').trim();
  if (!m) return fallback;
  const lower = m.toLowerCase();
  if (
    lower === 'request failed' ||
    lower === 'internal error' ||
    lower === 'internal server error' ||
    lower === 'empty response'
  ) {
    return fallback;
  }
  return m;
}

export function getToken(): string {
  return (localStorage.getItem('token') || '').trim();
}

export function getTenantId(): string {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null') as TenantUser | null;
    if (u?.tenantId) return u.tenantId;
  } catch {
    /* ignore */
  }
  return localStorage.getItem('tenant_id') || '';
}

export function authHeader(includeTenant = true): Record<string, string> {
  const t = getToken();
  const h: Record<string, string> = { Authorization: `Bearer ${t}` };
  if (includeTenant) {
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null') as TenantUser | null;
      if (u?.tenantId) h['X-Tenant-ID'] = u.tenantId;
    } catch {
      /* ignore */
    }
  }
  return h;
}

export function getCurrentUserName(): string {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}') as TenantUser;
    return u.name || u.display_name || u.username || 'operator';
  } catch {
    return 'operator';
  }
}

export function handleAuthFailure(resp: Response | null): boolean {
  if (!resp || resp.status !== 401) return false;
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('tenant_code');
  window.dispatchEvent(new Event('gridorbit:auth-expired'));
  return true;
}

export async function apiFetch<T = unknown>(
  url: string,
  opts: RequestInit & { body?: unknown } = {},
): Promise<ApiResponse<T>> {
  const path = url.startsWith('/') ? API + url : url;
  const headers: Record<string, string> = {
    ...authHeader(!path.includes('/api/v1/platform')),
    ...(opts.headers as Record<string, string>),
  };
  let body = opts.body as BodyInit | undefined;
  if (body != null && typeof body === 'object' && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(body);
  }
  const r = await fetch(path, { ...opts, headers, body });
  const text = await r.text();
  if (r.status === 401) {
    handleAuthFailure(r);
    try {
      return JSON.parse(text) as ApiResponse<T>;
    } catch {
      return { code: 401, message: '未登录或登录已过期' };
    }
  }
  if (!text) return { code: r.status, message: r.statusText || 'empty response' };
  try {
    return JSON.parse(text) as ApiResponse<T>;
  } catch {
    throw new Error(`${r.status ? `HTTP ${r.status}: ` : ''}${text.slice(0, 120)}`);
  }
}

export function sopPreviewFrameUrl(
  sopId: string,
  layoutIdOrOpts?:
    | string
    | {
        layoutId?: string;
        view?: 'viewport' | 'step_focus';
        step?: number;
        /** 用工位预览壳层；纸质版式会投影成编制预览同款视口 */
        station?: boolean;
      },
): string {
  const token = getToken();
  const tid = getTenantId();
  const opts =
    typeof layoutIdOrOpts === 'string' || layoutIdOrOpts == null
      ? { layoutId: layoutIdOrOpts as string | undefined }
      : layoutIdOrOpts;
  const view = opts.view || 'viewport';
  let layout = opts.layoutId || DEFAULT_STATION_LAYOUT;
  if (view === 'step_focus') {
    layout = 'step_focus_card_v1';
  } else if (opts.station !== false && view === 'viewport') {
    layout = resolveStationLayoutId(opts.layoutId || DEFAULT_STATION_LAYOUT);
  }
  const q = new URLSearchParams({
    embed: '1',
    preview_sop_id: sopId,
    token,
    tenant_id: tid,
    layout_id: layout,
    view,
    station: opts.station || view === 'step_focus' ? '1' : '0',
  });
  if (opts.step != null && opts.step > 0) {
    q.set('active_step', String(opts.step));
  }
  return `${window.location.origin}/station-preview/?${q.toString()}`;
}
